import React, {useEffect, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";

import {formatCurrency} from "../../utils/Utils";
import {DashboardService} from "../../service/DashboardService";
import {AppContext} from "../../HomePage";
import {sellersForMarketplace} from "../Product/marketplace/marketplaceProfile";

// Every seller of one marketplace for one product, against the product's own
// price. Used by Price Comparison and by the marketplace operator product list.
// It owns the fetch and a per-product cache: the endpoint returns all of a
// product's marketplaces at once, so a product is fetched once per mount.
// onLoadingChange(productId, marketplaceKey, isLoading) lets the opener show a
// loading hint on the cell that was clicked. label, when given, replaces the raw
// marketplace key in the dialog header (the marketplace profile passes a display
// label; Price Comparison does not, so it keeps showing the key).
const MarketplaceSellersDialog = ({visible, onHide, t, campaignId, product, marketplaceKey, label, onLoadingChange}) => {
    const {setError} = React.useContext(AppContext);
    // {productId: {marketplaceKey: [sellers]}}
    const [cache, setCache] = useState({});
    const [loading, setLoading] = useState({});
    const inFlight = useRef({});
    const productId = product ? product.id : null;

    useEffect(() => {
        if (!visible || !campaignId || productId == null || !marketplaceKey) return;
        if (cache[productId] && sellersForMarketplace(cache[productId], marketplaceKey).length > 0) return;
        if (inFlight.current[productId]) return;

        inFlight.current[productId] = true;
        const key = marketplaceKey;
        setLoading(prev => ({...prev, [productId]: true}));
        if (onLoadingChange) onLoadingChange(productId, key, true);
        new DashboardService().getPriceComparisonMarketplaceSellers(campaignId, productId)
            .then(data => {
                setCache(prev => ({...prev, [productId]: data || {}}));
            })
            .catch(error => {
                setError(error);
                setError(null);
            })
            .finally(() => {
                inFlight.current[productId] = false;
                setLoading(prev => ({...prev, [productId]: false}));
                if (onLoadingChange) onLoadingChange(productId, key, false);
            });
        // The cache is read, not watched: a fetch runs when a cell is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, campaignId, productId, marketplaceKey]);

    if (!product || !marketplaceKey) return null;
    const rowData = product;
    const isLoading = !!loading[productId];
    const allSellers = cache[productId] ? sellersForMarketplace(cache[productId], marketplaceKey) : null;

    return (
        <Dialog
            header={(label || marketplaceKey) + ' — ' + rowData.name}
            visible={visible}
            style={{ width: '800px' }}
            onHide={onHide}
        >
            {isLoading || !allSellers ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>{t('price_comparison.loading')}</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>{t('price_comparison.seller')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.price')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.shipping')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.total')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.list_price')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.mine')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.diff')}</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>{t('price_comparison.diff_pct')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allSellers.map((seller, index) => {
                            const shipping = seller.shipping_price || 0;
                            const total = seller.price + shipping;
                            const ownPrice = rowData.price;
                            const diff = ownPrice ? seller.price - ownPrice : null;
                            const diffPct = ownPrice && diff !== null ? ((diff / ownPrice) * 100).toFixed(1) : null;
                            const diffColor = diff === null ? '' : diff < 0 ? '#e53935' : diff > 0 ? '#43a047' : '';
                            const isMin = index === 0;
                            return (
                                <tr key={index} style={{ background: index % 2 === 0 ? '#f8f9fa' : '#fff', borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px' }}>
                                        {isMin && '⭐ '}
                                        {seller.product_url ? (
                                            <a href={seller.product_url} target="_blank" rel="noopener noreferrer">
                                                {seller.seller_name}
                                            </a>
                                        ) : seller.seller_name}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', color: isMin ? '#43a047' : '', fontWeight: isMin ? 'bold' : '' }}>
                                        {formatCurrency(seller.price, rowData.currency)}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                        {shipping > 0 ? formatCurrency(shipping, rowData.currency) : '—'}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                        {formatCurrency(total, rowData.currency)}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                        {seller.list_price ? formatCurrency(seller.list_price, rowData.currency) : '—'}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                        {ownPrice ? formatCurrency(ownPrice, rowData.currency) : '—'}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', color: diffColor }}>
                                        {diff !== null ? formatCurrency(diff, rowData.currency) : '—'}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', color: diffColor }}>
                                        {diffPct !== null ? diffPct + '%' : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </Dialog>
    );
};

export default MarketplaceSellersDialog;
